import React, { useState, useEffect } from 'react';
import {
  Users, User, Plus, Loader,
  Edit2,
} from 'lucide-react';
import { useDropdownData } from '../utils/useDropdownData';
import { useAuth, useActiveEstId } from './AuthContext';
import EmployeeEditForm from './EmployeeEditForm';
import EstablishmentCard from './EstablishmentCard';
import './MyProfile.css';
import './Employees.css';

// ── Helper: derive display name from employee object ─────────────────
const empName = (emp) =>
  emp.name ||
  `${emp.first_name || ''} ${emp.last_name || ''}`.trim() ||
  emp.employee_id ||
  'Unknown';

// ── Status badge colour ───────────────────────────────────────────────
const statusClass = (status) => {
  if (!status || status === 'active') return 'emp-status-active';
  if (status === 'inactive' || status === 'terminated') return 'emp-status-inactive';
  return 'emp-status-other';
};

// ── Main component ────────────────────────────────────────────────────
export default function Employees() {
  const { user } = useAuth();
  // If ClientAdmin, lock to their assigned establishment
  const lockedEstId = useActiveEstId();

  // mode: 'list' | 'edit' | 'new'
  const [mode, setMode]           = useState('list');
  const [selectedEstId, setSelectedEstId] = useState(lockedEstId || '');
  const [editingEmpId, setEditingEmpId]   = useState(null);

  const {
    establishments,
    employees,
    loading,
    fetchEstablishments,
    fetchEmployeesByEstablishment,
    clearEmployees,
  } = useDropdownData();

  useEffect(() => {
    if (lockedEstId) {
      setSelectedEstId(lockedEstId);
      fetchEmployeesByEstablishment(lockedEstId);
    } else {
      fetchEstablishments();
    }
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEstChange = (e) => {
    const id = e.target.value;
    setSelectedEstId(id);
    if (id) {
      fetchEmployeesByEstablishment(id);
    } else {
      clearEmployees();
    }
  };

  const openEdit = (emp) => {
    // UpdateEmployee uses employee_id (string code) as the select value, not the numeric id
    setEditingEmpId(emp.employee_id || emp.id);
    setMode('edit');
  };

  const openNew = () => {
    setEditingEmpId(null);
    setMode('new');
  };

  const goBack = () => {
    setMode('list');
    setEditingEmpId(null);
    // Refresh employee list after a save/create
    if (selectedEstId) fetchEmployeesByEstablishment(selectedEstId);
  };

  const estName = establishments.find(e => String(e.id) === String(selectedEstId))?.name || '';

  // ── EDIT MODE ─────────────────────────────────────────────────────
  if (mode === 'edit') {
    return (
      <EmployeeEditForm
        establishmentId={selectedEstId}
        establishmentName={estName}
        employeeId={editingEmpId}
        onBack={goBack}
      />
    );
  }

  // ── NEW MODE ──────────────────────────────────────────────────────
  if (mode === 'new') {
    return (
      <EmployeeEditForm
        establishmentId={selectedEstId}
        establishmentName={estName}
        onBack={goBack}
      />
    );
  }

  // ── LIST MODE ─────────────────────────────────────────────────────
  return (
    <div className="mp-page">
      {/* Toolbar */}
      <div className="est-toolbar">
        <h1 className="est-page-title">Employees</h1>
        {selectedEstId && (
          <button className="mp-btn-edit" onClick={openNew}>
            <Plus size={15} /> New Employee
          </button>
        )}
      </div>

      {/* Establishment selector — hidden for ClientAdmin (auto-scoped to their org) */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstId}
          onChange={handleEstChange}
          loading={loading.establishments}
        />
      )}

      {/* Employee list */}
      {!selectedEstId && !lockedEstId ? (
        <div className="mp-state-center" style={{ marginTop: '2rem' }}>
          <Users size={36} />
          <p>Select an organization to view its employees.</p>
        </div>
      ) : loading.employees ? (
        <div className="mp-state-center" style={{ marginTop: '2rem' }}>
          <Loader size={28} className="mp-spinner" />
          <p>Loading employees…</p>
        </div>
      ) : employees.length === 0 ? (
        <div className="mp-state-center" style={{ marginTop: '2rem' }}>
          <User size={36} />
          <p>No employees found for this organization.</p>
        </div>
      ) : (
        <div className="est-list" style={{ marginTop: '1.25rem' }}>
          {employees.map(emp => {
            const initials = (empName(emp) || 'U')
              .split(' ')
              .slice(0, 2)
              .map(w => w[0] ?? '')
              .join('')
              .toUpperCase();
            return (
              <div key={emp.id} className="est-card">
                {/* Avatar */}
                <div className="emp-card-avatar">{initials}</div>

                {/* Info */}
                <div className="est-card-body">
                  <div className="est-card-name">{empName(emp)}</div>
                  <div className="est-card-meta">
                    {emp.employee_id && <span>ID: {emp.employee_id}</span>}
                    {emp.designation && (
                      <>
                        <span className="est-dot">·</span>
                        <span>{emp.designation}</span>
                      </>
                    )}
                    {emp.department && (
                      <>
                        <span className="est-dot">·</span>
                        <span>{emp.department}</span>
                      </>
                    )}
                    {emp.employment_type && (
                      <>
                        <span className="est-dot">·</span>
                        <span>{emp.employment_type}</span>
                      </>
                    )}
                    {emp.employment_status && (
                      <>
                        <span className="est-dot">·</span>
                        <span className={`emp-status-badge ${statusClass(emp.employment_status)}`}>
                          {emp.employment_status}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Edit button */}
                <button className="mp-btn-edit" onClick={() => openEdit(emp)}>
                  <Edit2 size={14} /> Edit
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Errors */}
      {loading.employees === false && !loading.establishments && selectedEstId && (
        employees.length === 0 ? null : null
      )}
    </div>
  );
}
